import { z } from 'zod'

/**
 * Project plugins — remote MCP servers per project (board card wwfu).
 *
 * One connection per project, authorised once by an owner/admin, used by
 * every member through HUB. The public row shape here never carries a
 * secret: tokens, API keys and DCR client secrets stay encrypted inside the
 * ProjectStore DO.
 */

/** At most this many plugins per project. */
export const MCP_MAX_SERVERS_PER_PROJECT = 5
/** Slug = the tool-name namespace (`mcp__<slug>__<tool>`), immutable. */
export const MCP_SLUG_MAX = 12
/** Display name, editable. */
export const MCP_NAME_MAX = 40
/** Longest URL we accept for an MCP endpoint. */
export const MCP_URL_MAX = 2048
/** Discovery stores at most this many tools; more ⇒ status `error`. */
export const MCP_MAX_TOOLS = 100
/** Anthropic's tool-name rule — one bad name fails the whole request. */
export const MCP_TOOL_NAME_MAX = 64
export const MCP_TOOL_NAME_PATTERN = /^[a-zA-Z0-9_-]{1,64}$/
/** Slug charset. */
export const MCP_SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,10}[a-z0-9])?$/

export const mcpServerStatusSchema = z.enum(['active', 'needs_auth', 'needs_reauth', 'error'])
export type McpServerStatus = z.infer<typeof mcpServerStatusSchema>

export const mcpAuthKindSchema = z.enum(['none', 'bearer', 'oauth'])
export type McpAuthKind = z.infer<typeof mcpAuthKindSchema>

/** Why a discovered tool is not mounted. */
export const mcpToolUnusableReasonSchema = z.enum(['name_too_long'])

export const mcpToolSchema = z.object({
  name: z.string(),
  title: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  usable: z.boolean(),
  reason: mcpToolUnusableReasonSchema.optional(),
})
export type McpTool = z.infer<typeof mcpToolSchema>

/** The public plugin row — what the pane and procedures see. Never a secret. */
export const mcpServerSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  url: z.string(),
  authKind: mcpAuthKindSchema,
  enabled: z.boolean(),
  status: mcpServerStatusSchema,
  tools: z.array(mcpToolSchema),
  toolsCheckedAt: z.string().nullable(),
  lastError: z.string().nullable(),
  lastUsedAt: z.string().nullable(),
  authorizedBy: z.string().nullable(),
  authorizedByName: z.string().nullable(),
  authorizedAt: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type McpServer = z.infer<typeof mcpServerSchema>

/** Same sanitising as Flue's `createToolName` (mcp.ts `sanitizeToolNamePart`). */
export function sanitizeMcpToolNamePart(value: string): string {
  return value.replace(/[^A-Za-z0-9_-]/g, '_').replace(/^_+|_+$/g, '') || 'unnamed'
}

/**
 * Display name → slug: lower-case ASCII, `[a-z0-9-]`, ≤ MCP_SLUG_MAX, no
 * leading/trailing hyphen. Diacritics are folded (å → a). Empty ⇒ `plugin`.
 */
export function slugifyMcpName(name: string): string {
  const folded = name
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  const cut = folded.slice(0, MCP_SLUG_MAX).replace(/-+$/g, '')
  return cut || 'plugin'
}

/** Make `base` unique against `taken` by suffixing `-2`, `-3`, … within MCP_SLUG_MAX. */
export function uniqueMcpSlug(base: string, taken: Iterable<string>): string {
  const used = new Set(taken)
  if (!used.has(base)) return base
  for (let n = 2; n < 1000; n++) {
    const suffix = `-${n}`
    const head = base.slice(0, MCP_SLUG_MAX - suffix.length).replace(/-+$/g, '')
    const candidate = `${head || 'p'}${suffix}`
    if (!used.has(candidate)) return candidate
  }
  throw new Error('no free slug')
}

/** The name the model sees for a server's tool — exactly what Flue builds. */
export function adaptedToolName(slug: string, tool: string): string {
  return `mcp__${sanitizeMcpToolNamePart(slug)}__${sanitizeMcpToolNamePart(tool)}`
}

/** Does the adapted name pass the provider's tool-name rule? */
export function fitsToolNameBudget(slug: string, tool: string): boolean {
  return MCP_TOOL_NAME_PATTERN.test(adaptedToolName(slug, tool))
}

/** `mcp__<slug>__<tool>` → its parts; null for any other tool name. */
export function parseMcpToolName(name: string): { server: string; tool: string } | null {
  const match = /^mcp__([A-Za-z0-9-]+)__(.+)$/.exec(name)
  if (!match) return null
  return { server: match[1]!, tool: match[2]! }
}

/**
 * Board wwfu REVIEW-1 F1: many hosted MCP servers put a per-account secret in
 * the URL path or query (`https://mcp.zapier.com/api/mcp/s/<secret>/mcp`). A
 * member who could read it could call the server outside HUB as the admin.
 * Members therefore only ever see the origin.
 */
export function redactMcpUrl(url: string): string {
  try {
    const parsed = new URL(url)
    const hasMore = parsed.pathname.replace(/\/+$/, '') !== '' || parsed.search || parsed.hash
    return `${parsed.origin}${hasMore ? '/…' : ''}`
  } catch {
    return '…'
  }
}

/** Every URL inside free text (an error message) reduced to its origin. */
export function redactUrlsInText(text: string): string {
  return text.replace(/\bhttps?:\/\/[^\s"'<>`)\]]+/gi, (match) => redactMcpUrl(match))
}

/** The member view of a plugin row: origin only, URLs in the last error redacted. */
export function mcpServerForMember(server: McpServer): McpServer {
  return {
    ...server,
    url: redactMcpUrl(server.url),
    lastError: server.lastError === null ? null : redactUrlsInText(server.lastError),
  }
}
